import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { Nullable } from 'corgi-types';

interface ScrollToTargetOptions {
  scrollOnError: boolean;
  verticalPadding: number;
  duration?: number;
  messageId: number;
  element: Nullable<HTMLElement>;
  containerRef?: MutableRefObject<Nullable<HTMLElement>>;
  shouldScrollIntoView?: boolean;
}

interface AnimateLoopOptions {
  timestamp: number;
  startTime: number;
  startPosition: number;
  endPosition: number;
  scrollDuration: number;
  containerRef?: MutableRefObject<Nullable<HTMLElement>>;
}

interface ElevateOptions {
  startPosition: number;
  endPosition: number;
  scrollDuration: number;
  containerRef?: MutableRefObject<Nullable<HTMLElement>>;
}

const easeOut = (
  currentTime: number,
  startPosition: number,
  positionChange: number,
  duration: number
  // eslint-disable-next-line max-params
) => -positionChange * (currentTime / duration) * (currentTime / duration - 2) + startPosition;

const getVerticalOffset = (
  targetElement: Nullable<HTMLElement>,
  verticalPadding: number,
  containerElement?: Nullable<HTMLElement>
) => {
  let verticalOffset = 0;

  if (containerElement) {
    verticalOffset = (targetElement?.offsetTop ?? 0) - containerElement.offsetTop;
  } else {
    while (targetElement) {
      verticalOffset += targetElement.offsetTop || 0;
      targetElement = targetElement.offsetParent as Nullable<HTMLElement>;
    }
  }

  if (verticalPadding) {
    verticalOffset = verticalOffset - verticalPadding;
  }

  return verticalOffset;
};

const animateLoop = ({
  timestamp,
  startTime,
  startPosition,
  endPosition,
  scrollDuration,
  containerRef,
}: AnimateLoopOptions) => {
  const timeSoFar = timestamp - startTime;

  const element = containerRef?.current ?? window;

  element.scrollTo(
    0,
    easeOut(timeSoFar, startPosition, endPosition - startPosition, scrollDuration)
  );

  if (timeSoFar < scrollDuration) {
    requestAnimationFrame((timestampCurrent: number) =>
      animateLoop({
        timestamp: timestampCurrent,
        startTime,
        startPosition,
        endPosition,
        scrollDuration,
        containerRef,
      })
    );
  }
};

export const elevate = ({
  startPosition,
  endPosition,
  scrollDuration,
  containerRef,
}: ElevateOptions) => {
  requestAnimationFrame((timestamp: number) =>
    animateLoop({
      timestamp,
      startTime: timestamp,
      startPosition,
      endPosition,
      scrollDuration,
      containerRef,
    })
  );
};

/**
Inspired by: https://github.com/tholman/elevator.js
from file: https://github.com/tholman/elevator.js/blob/master/elevator.js
with modifications- written in TypeScript and in more modern syntax.
 */
export const useScrollToTarget = ({
  scrollOnError,
  verticalPadding,
  duration,
  element,
  messageId,
  containerRef,
  shouldScrollIntoView = false,
}: ScrollToTargetOptions) => {
  const previousMessageId = useRef(-1);

  useEffect(() => {
    if (element && shouldScrollIntoView) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });

      return;
    }

    if (element && scrollOnError && previousMessageId.current !== messageId) {
      previousMessageId.current = messageId;

      const startPosition =
        containerRef?.current?.scrollTop ??
        (document.documentElement.scrollTop || document.body.scrollTop);

      const endPosition = getVerticalOffset(element, verticalPadding, containerRef?.current);
      const scrollDuration = duration ?? Math.abs(endPosition - startPosition) * 1.5;

      elevate({
        startPosition,
        endPosition,
        scrollDuration,
        containerRef,
      });
    }
  }, [element, messageId, scrollOnError]);
};
